<script lang="ts">
	// UC-63: a shop's cash-point shifts, newest first, and one shift's report.
	// Read-only: the cash point's store opens and closes shifts; the dashboard
	// shows the figures the server recomputes from their stamped payments.
	// The shops and tills are the Dashboard's reads, shared with the rail.
	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import ShiftReport from './ShiftReport.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { fill, message, type Language } from '../i18n/messages';
	import { overShort } from '../format/shift';
	import { formatWhen } from '../format/time';
	import { loadShops, loadTills, ownerShops, ownerTills } from '../owner/data.svelte';
	import type { Account } from '../owner/session.svelte';

	let { language, account }: { language: Language; account: Account } = $props();
	const shops = $derived(ownerShops());
	const tills = $derived(ownerTills());
	let pickedShop = $state('');
	let tillId = $state('');
	const shopId = $derived(pickedShop || shops.value[0]?.id || '');
	const cashPoints = $derived(
		tills.value.filter((till) => till.shopId === shopId && !till.cashPointTillId),
	);
	let rows = $state<components['schemas']['ShiftBody'][]>([]);
	let next = $state<string | undefined>();
	let loading = $state(false);
	let failure = $state('');
	// Try again only after the network or the server failed (web.md, Errors).
	let retryable = $state(false);
	let failedBefore: string | undefined;
	let report = $state({ id: '', tillName: '' });
	let asked = 0;

	async function load(shop: string, till: string, before?: string) {
		const ticket = ++asked;
		loading = true;
		failure = '';
		retryable = false;
		failedBefore = before;
		if (!before) {
			rows = [];
			next = undefined;
		}
		try {
			const { data, error, response } = await api.GET('/api/owner/shifts', {
				params: { query: { shopId: shop, tillId: till, before } },
			});
			if (ticket !== asked) return;
			if (error !== undefined || !data) {
				failure = error?.message ?? message(language, 'loadFailed');
				retryable = response.status >= 500;
				return;
			}
			rows = before ? [...rows, ...(data.shifts ?? [])] : (data.shifts ?? []);
			next = data.next;
		} catch {
			if (ticket !== asked) return;
			failure = message(language, 'loadFailed');
			retryable = true;
		} finally {
			if (ticket === asked) loading = false;
		}
	}

	$effect(() => {
		const shop = shopId;
		const till = tillId;
		if (shop) untrack(() => void load(shop, till));
		return () => {
			++asked;
		};
	});

	function when(iso: string): string {
		return formatWhen(iso, language, {
			today: message(language, 'reportsToday'),
			yesterday: message(language, 'reportsYesterday'),
		});
	}
	const columns = $derived([
		{ label: message(language, 'shiftsCashPoint'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'shiftsOpened'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'shiftsClosed'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'shiftsOverShort'), w: '120px' },
		{ label: message(language, 'shiftsLate'), w: '80px' },
		{ label: '', w: '84px' },
	]);
</script>

<div class="shifts" data-testid="panel-shifts">
	{#if !shops.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else if shops.failure && shops.value.length === 0}
		<Band
			tone="flag"
			testId="shifts-error"
			action={shops.retryable ? message(language, 'retry') : undefined}
			actionTestId="shifts-retry"
			onAction={() => void loadShops(message(language, 'loadFailed'))}>{shops.failure}</Band
		>
	{:else if shops.value.length === 0}
		<Card pad testId="shifts-no-shops">{message(language, 'tillsNoShops')}</Card>
	{:else}
		<div class="filters">
			<Segmented
				label={message(language, 'ordersShop')}
				options={shops.value.map((shop) => ({
					value: shop.id,
					label: shop.name,
					testId: `shifts-shop-${shop.id}`,
				}))}
				value={shopId}
				onChange={(id) => {
					pickedShop = id;
					tillId = '';
				}}
			/>
			<Segmented
				label={message(language, 'shiftsCashPoint')}
				options={[
					{ value: '', label: message(language, 'shiftsAllCashPoints'), testId: 'shifts-till-all' },
					...cashPoints.map((till) => ({
						value: till.id,
						label: till.name,
						testId: `shifts-till-${till.id}`,
					})),
				]}
				value={tillId}
				onChange={(id) => (tillId = id)}
			/>
		</div>
		{#if tills.failure}
			<!-- Without the tills, the cash points to pick from are unknown. -->
			<Band
				tone="flag"
				testId="shifts-tills-error"
				action={tills.retryable ? message(language, 'retry') : undefined}
				actionTestId="shifts-tills-retry"
				onAction={() => void loadTills(message(language, 'loadFailed'))}>{tills.failure}</Band
			>
		{/if}
		{#if failure}
			<Band
				tone="flag"
				testId="shifts-error"
				action={retryable ? message(language, 'retry') : undefined}
				actionTestId="shifts-retry"
				actionBusy={loading}
				onAction={() => void load(shopId, tillId, failedBefore)}>{failure}</Band
			>
		{/if}
		{#if loading && rows.length === 0}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'loading')}
					caption={message(language, 'loading')}
				/>
			</div>
		{:else if rows.length === 0 && !failure}
			<Card pad testId="shifts-empty">{message(language, 'shiftsEmpty')}</Card>
		{:else if rows.length > 0}
			<TablePanel
				{columns}
				title={message(language, 'ownerShifts')}
				note={message(language, 'shiftsNote')}
				testId="shifts-list"
			>
				{#each rows as shift (shift.id)}
					<Row testId="shift-row-{shift.id}">
						<span>{shift.tillName}</span>
						<span>{when(shift.openedAt)}</span>
						<span
							>{#if shift.closedAt}{when(shift.closedAt)}{:else}<Chip
									text={message(language, 'shiftsOpenChip')}
									tone="flag"
									dot
									testId="shift-open-{shift.id}"
								/>{/if}</span
						>
						<span data-testid="shift-overshort-{shift.id}"
							>{#if shift.closedAt && shift.overShortMinor !== undefined}{overShort(
									language,
									shift.overShortMinor,
									account.settings.minorDigits,
								)}{/if}</span
						>
						<span data-testid="shift-late-{shift.id}"
							>{#if shift.lateCount > 0}{fill(
									message(language, 'shiftsLateCount'),
									shift.lateCount,
								)}{/if}</span
						>
						<Button
							label={message(language, 'ordersDetails')}
							tone="text-quiet"
							size="sm"
							testId="shift-open-report-{shift.id}"
							onPress={() => (report = { id: shift.id, tillName: shift.tillName })}
						/>
					</Row>
				{/each}
			</TablePanel>
		{/if}
		{#if next}
			<Button
				label={message(language, 'shiftsShowOlder')}
				tone="secondary"
				testId="shifts-older"
				state={loading ? 'pending' : 'idle'}
				pendingLabel={message(language, 'shiftsOlderLoading')}
				onPress={() => void load(shopId, tillId, next)}
			/>
		{/if}
	{/if}
</div>

<ShiftReport
	{language}
	minorDigits={account.settings.minorDigits}
	shiftId={report.id}
	tillName={report.tillName}
	onClose={() => (report = { id: '', tillName: '' })}
/>

<style>
	.shifts {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}
	.filters {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
	}
	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}
</style>
